import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  PinwheelCardInput,
  PinwheelExperienceInput,
} from '@varo/graphql-schema-types';
import backgroundTextureSunset from 'assets/images/vxl/component/productIntroScreen/background-texture-sunset.png';
import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import UnfreezeCardsBottomSheet from 'features/billSwitch/components/UnfreezeCardsBottomSheet/UnfreezeCardsBottomSheet';
import { logErrorEvent } from 'features/billSwitch/helpers/logErrorEvent';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItemGroup from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { computeBillsTotal } from 'shared/helpers/computeBillsTotal/computeBillsTotal';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import { useFetchCardsWithLockStatus } from 'shared/hooks/useFetchCardsWithLockStatus/useFetchCardsWithLockStatus';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

interface BillSwitchProductIntroScreenProps {
  believeVariant?: boolean;
}

const cardOptionsForVariant = (
  isBelieveVariant: boolean,
): PinwheelCardInput[] => {
  // believe only supports Believe cards
  // control case user can pick any card they have
  return isBelieveVariant
    ? [PinwheelCardInput.Believe]
    : [PinwheelCardInput.Debit, PinwheelCardInput.Believe];
};

const BillSwitchProductIntroScreen = ({
  believeVariant,
}: BillSwitchProductIntroScreenProps) => {
  const isBelieveVariant = believeVariant ?? false;
  const { defaultTitle, defaultMessage, retryButtonTitle, doneButtonTitle } =
    useDefaultErrorStrings();

  const { t: translateErrorText } = useTranslation('billSwitch', {
    keyPrefix: 'featureErrors',
  });
  const { t: translatedText } = useTranslation('billSwitch', {
    keyPrefix: 'productIntroScreen',
  });

  const { segmentClient } = useAnalyticsConfiguration();
  const isBillSwitchEnabled = useIsFeatureEnabled(
    FeatureFlagKey.BILL_SWITCH_PILOT,
  );
  const { top } = useSafeAreaInsets();
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();
  const { setSnackBarMessage } = useContext(GlobalUIContext);

  const router = useRouter();

  const {
    cards,
    hasCards,
    hasLockedCards,
    analyticsMetadata: { isDebitLocked, isBelieveLocked },
    cardsError,
    cardsFetching,
    refetchCards,
  } = useFetchCardsWithLockStatus({
    believeOnly: isBelieveVariant,
    pause: !isBillSwitchEnabled,
  });

  const sourceName = isBelieveVariant ? 'home_believe' : 'home_control';
  const canLaunchPinwheel = hasCards && !hasLockedCards;

  useViewDidFocusLogScreenAnalytics({
    name: 'Bill Switch Product Intro',
    properties: {
      category: 'bill_switch',
      source_name: sourceName,
    },
  });

  useFocusEffect(
    useCallback(() => {
      if (!isBillSwitchEnabled) {
        logErrorEvent({
          segmentClient,
          source_name: sourceName,
          error_message: '[Feature Flag]: disabled',
        });
      }
    }, [isBillSwitchEnabled, segmentClient, sourceName]),
  );

  useEffect(() => {
    if (cardsError) {
      logErrorEvent({
        segmentClient,
        source_name: sourceName,
        error_message:
          '[Fetch Cards Error]: ' + (cardsError?.message ?? 'unknown'),
      });
    }
  }, [cardsError, segmentClient, sourceName]);

  const launchPinwheel = () => {
    if (!canLaunchPinwheel) {
      return;
    }
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.BillSwitch,
        cardOptions: cardOptionsForVariant(isBelieveVariant),
      },
      sourceName,
      onLinkTokenError: () => {
        logErrorEvent({
          segmentClient,
          source_name: sourceName,
          error_message: '[Link Token Error]: Link token generation failed',
        });
        setSnackBarMessage?.(
          translateErrorText('unableToLaunchPinwheel.message'),
        );
      },
      onOutcome: ({ sessionState }) => {
        const { billSwitches } = sessionState ?? {};
        if (billSwitches?.length) {
          const pathname = believeVariant
            ? '/bill-switch/believe/funding'
            : '/bill-switch/funding';

          const amount = computeBillsTotal(billSwitches);
          router.replace({
            pathname,
            params: { amt: amount },
          });
        }
      },
    });
  };

  const hasError = !isBillSwitchEnabled || cardsError;
  const errorTitle = isBillSwitchEnabled
    ? (cardsError?.title ?? defaultTitle)
    : translateErrorText('featureUnavailable.title');
  const errorMessage = isBillSwitchEnabled
    ? (cardsError?.message ?? defaultMessage)
    : translateErrorText('featureUnavailable.message');
  const featureBulletsKey = isBelieveVariant
    ? 'believeFeatureBullets'
    : 'featureBullets';
  const featureBulletItems = isBelieveVariant
    ? [
        { key: 'one', iconKey: 'CASHBACK' },
        { key: 'two', iconKey: 'FLASH_ON' },
        { key: 'three', iconKey: 'STAR' },
      ]
    : [
        { key: 'one', iconKey: 'FLASH_ON' },
        { key: 'two', iconKey: 'STAR' },
        { key: 'three', iconKey: 'CASHBACK' },
      ];

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
              transparent
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={cardsFetching && isBillSwitchEnabled}
        LoadingComponent={
          <View style={{ paddingTop: top + theme.spacing.m }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={errorTitle}
              messageBody={errorMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: navigateBack,
              }}
              secondaryButton={
                !isBillSwitchEnabled
                  ? undefined
                  : {
                      title: retryButtonTitle,
                      onPress: refetchCards,
                    }
              }
            />
          ) : null
        }
      />

      {isBillSwitchEnabled && !cardsFetching ? (
        <ProductIntroScreen
          visual="BILLS_A"
          headlineTextStyle="expressive"
          backgroundTexture={backgroundTextureSunset as ImageSourcePropType}
          headlineText={
            isBelieveVariant
              ? translatedText('believe.title')
              : translatedText('control.title')
          }
          topPadding={70}
          primaryButton={{
            label: translatedText('cta.primary'),
            onPress: () => {
              if (!canLaunchPinwheel) {
                bottomSheetRef?.current?.present();
              } else {
                launchPinwheel();
              }
            },
            isLoading: isPinwheelLoading,
            analyticsId: 'launchPinwheel',
            customAttributes: {
              source_name: isBelieveVariant ? 'home_believe' : 'home_control',
              debit_locked: isDebitLocked,
              believe_locked: isBelieveLocked,
            },
          }}
          bodyContent={
            <View style={styles.bodyContainer}>
              <View style={styles.bodyTextContainer}>
                <BodyBaseText numberOfLines={0}>
                  {isBelieveVariant
                    ? translatedText('believe.subtitle')
                    : translatedText('control.subtitle')}
                </BodyBaseText>
              </View>
              <ListItemGroup
                items={featureBulletItems.map((item) => ({
                  primaryCopy: translatedText(
                    `${featureBulletsKey}.${item.key}`,
                  ),
                  leadingAccessory: {
                    id: `item-${item.key}-icon`,
                    name: VXLComponentId.ICON_AVATAR,
                    props: { variant: 'MD', iconKey: item.iconKey },
                  },
                  showsChevron: false,
                }))}
              />
              <View style={styles.bodyTextContainer}>
                <BodyXSText numberOfLines={0}>
                  {translatedText('productInfoFooter')}
                </BodyXSText>
              </View>
            </View>
          }
        />
      ) : null}
      <UnfreezeCardsBottomSheet
        ref={bottomSheetRef}
        useBelieveLanguage={isBelieveVariant}
        hasLockedCards={hasLockedCards}
        hasCards={hasCards}
        cards={cards}
        analyticsMetadata={{ source_name: sourceName }}
        onSuccess={() => {
          bottomSheetRef?.current?.dismiss();
          launchPinwheel();
        }}
        onDismiss={() => bottomSheetRef?.current?.dismiss()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  bodyTextContainer: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default BillSwitchProductIntroScreen;
